import { EmptyObject } from '@crossbeam/types';

import { sortBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import useAuth from '@/composables/useAuth';
import { PREVENT_DEFAULT_PARTNERSTACK_COL } from '@/constants/feature_flags';
import { useCollaborateStore, useFeatureFlagStore } from '@/stores';
import {
  SharedList,
  SharedListUser,
  SharedListUserOrgAndLabel,
} from '@/types/shared_lists';

export default function useSharedList(givenListId?: string) {
  const route = useRoute();
  const listId = computed(() => givenListId || (route.params.id as string));

  const collaborateStore = useCollaborateStore();
  const { lists } = storeToRefs(collaborateStore);

  const featureFlags = useFeatureFlagStore();

  const { currentOrg, currentUser } = useAuth();

  const hasPreventPartnerStackColumn = computed(() =>
    featureFlags.hasFeatureFlag(PREVENT_DEFAULT_PARTNERSTACK_COL),
  );

  const currentList = computed(
    () => lists.value?.find((list) => list.id === listId.value) as SharedList,
  );

  const partnerOrg = computed(() =>
    orgsList.value?.find((org) => org.organization_id !== currentOrg.value.id),
  );
  const currentOrgInfoFromList = computed(() =>
    orgsList.value?.find((org) => org.organization_id === currentOrg.value.id),
  );

  function listUserName(user: SharedListUser | EmptyObject = {}) {
    if (user.first_name || user.last_name) {
      return [user.first_name, user.last_name]
        .reduce((fullName: string[], name) => {
          if (name) fullName.push(name);
          return fullName;
        }, [])
        .join(' ');
    }
    return 'Admin';
  }

  const teamMembersInCurrentList = computed(() => {
    const usersInList = collaborateStore
      .getListUsersInOrg(listId.value as string, currentOrg.value.id)
      .map((user) => ({
        ...user,
        org: currentOrgInfoFromList.value,
        label: listUserName(user),
      }));
    const sortedUsers = sortBy(usersInList, ['label']);
    const currentUserIndex = sortedUsers.findIndex(
      (user) => user.user_id === currentUser.value.id,
    );
    if (currentUserIndex !== -1) {
      const user = sortedUsers.splice(currentUserIndex, 1);
      sortedUsers.unshift(...user);
    }
    return sortedUsers;
  });

  const partnerTeamMembersInCurrentList = computed(() =>
    sortBy(
      collaborateStore
        .getListUsersInOrg(
          listId.value as string,
          partnerOrg.value?.organization_id,
        )
        .map((user) => ({
          ...user,
          org: partnerOrg.value,
          label: `${user.first_name} ${user?.last_name}`,
        })),
      ['label'],
    ),
  );

  const allUsersInList = computed<SharedListUserOrgAndLabel[]>(() => {
    return [
      ...teamMembersInCurrentList.value,
      ...partnerTeamMembersInCurrentList.value,
    ];
  });

  const orgsList = computed(() => currentList.value?.permissions);
  return {
    currentList,
    orgsList,
    partnerTeamMembersInCurrentList,
    teamMembersInCurrentList,
    partnerOrg,
    allUsersInList,
    hasPreventPartnerStackColumn,
    maxListRowsLimit: 250,
  };
}
